import React from 'react';
import { Avatar, NamedLink } from '../../components';
import { IoMdChatbubbles } from 'react-icons/io';

import { formatDate } from '../../util/dates';
import { ATTACHMENTS_ONLY_MESSAGE } from '../ActivityFeed/ActivityFeed';
import {
  TRANSITION_CONFIRM_PAYMENT,
  TRANSITION_CONFIRM_PROJECT,
  TRANSITION_RELEASE_FUNDS,
  TRANSITION_JOB_DONE_RELEASE_FUNDS,
  TRANSITION_REVIEW_1_BY_CUSTOMER,
  TRANSITION_REVIEW_2_BY_CUSTOMER,
  TRANSITION_REVIEW_1_BY_PROVIDER,
  TRANSITION_REVIEW_2_BY_PROVIDER,
  TRANSITION_JOB_DONE,
  TRANSITION_JOB_DONE_CUSTOMER,
  TRANSITION_JOB_DONE_PRO,
  TRANSITION_MAKE_PROJECT_BID_AFTER_ENQUIRY,
  TRANSITION_ENQUIRE,
  TRANSITION_REQUEST_PAYMENT,
  TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY,
  TRANSITION_MAKE_CUSTOM_OFFER,
  TRANSITION_MAKE_CUSTOM_PRO_OFFER,
  TRANSITION_ACCEPT_CUSTOM_PRO_OFFER,
  TRANSITION_MAKE_PROJECT_BID,
  TRANSITION_ACCEPT_OFFER,
  TRANSITION_DECLINE_OFFER,
  TRANSITION_EXPIRE_OFFER,
  TRANSITION_ACCEPT_APPLICATION,
  TRANSITION_SKIP_APPLICATION,
  TRANSITION_ACCEPT_AFTER_SKIP,
  TRANSITION_EXPIRE_ENQUIRY,
  TRANSITION_EXPIRE_APPLICATION,
  TRANSITION_JOB_ACCEPTED,
  TRANSITION_SKIP_TO_REVIEWS,
  TRANSITION_ACCEPT,
  TRANSITION_DECLINE,
  TRANSITION_EXPIRE_PAYMENT,
  TRANSITION_CANCEL_PAYMENT,
  TRANSITION_EXPIRE,
  TRANSITION_CANCEL,
  TRANSITION_COMPLETE,
  TRANSITION_CANCEL_BY_PROVIDER,
  TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE,
  TRANSITION_JOB_DONE_INVOICE,
  TRANSITION_ACCEPT_JOB_INVOICE,
  TRANSITION_JOB_DONE_ACCEPT_INVOICE,
  TRANSITION_CANCEL_INVOICE,
} from '../../util/transaction';

import css from './NotificationFeed.css';

const PROVIDER = 'provider';
const CUSTOMER = 'customer';

const NotificationFeed = ({
  currentUser,
  currentUserNotificationTxs,
  notifications,
  isFreelancer,
  intl,
}) => {
  const allMessagesMessage = intl.formatMessage({
    id: 'NotificationFeed.allMessages',
  });

  const fetchedNotifications = notifications.filter(item =>
    currentUserNotificationTxs.some(tx => tx.id.uuid === item.txId)
  );

  return (
    <div className={css.container}>
      <NamedLink
        className={css.linkToInbox}
        name="InboxPage"
        params={{ tab: isFreelancer ? 'sales' : 'orders' }}
      >
        <span>{allMessagesMessage}</span>
        <IoMdChatbubbles />
      </NamedLink>
      {fetchedNotifications.map(item => {
        const transaction = currentUserNotificationTxs.find(tx => tx.id.uuid === item.txId);
        return transaction ? (
          <NotificationFeedItem
            key={item.txId}
            currentUser={currentUser}
            message={item}
            transaction={transaction}
            intl={intl}
          />
        ) : null;
      })}
    </div>
  );
};

const NotificationFeedItem = ({ currentUser, message, transaction, intl }) => {
  const sender =
    currentUser.id.uuid === transaction.provider.id.uuid
      ? transaction.customer
      : transaction.provider;

  const transactionRole =
    transaction.provider.id.uuid === currentUser.id.uuid ? PROVIDER : CUSTOMER;
  const isCustomerRole = transactionRole === CUSTOMER;

  // No messages in transition notifications
  const isMessage = message.message !== undefined;

  const findTransitionTranslation = transition => {
    switch (transition) {
      // Enquiry and initial transitions
      case TRANSITION_ENQUIRE:
        return intl.formatMessage({ id: 'NotificationFeed.newEvent' });
      case TRANSITION_REQUEST_PAYMENT:
      case TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY:
        return intl.formatMessage({ id: 'NotificationFeed.newEvent' });

      // Offer-related transitions
      case TRANSITION_MAKE_PROJECT_BID:
      case TRANSITION_MAKE_PROJECT_BID_AFTER_ENQUIRY:
      case TRANSITION_MAKE_CUSTOM_OFFER:
      case TRANSITION_MAKE_CUSTOM_PRO_OFFER:
        return intl.formatMessage({ id: 'NotificationFeed.newOffer' });

      // Accept/Confirm transitions
      case TRANSITION_CONFIRM_PAYMENT:
      case TRANSITION_CONFIRM_PROJECT:
      case TRANSITION_ACCEPT:
      case TRANSITION_ACCEPT_OFFER:
      case TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE:
      case TRANSITION_ACCEPT_CUSTOM_PRO_OFFER:
        return intl.formatMessage({ id: 'NotificationFeed.acceptOffer' });

      // Decline/Cancel transitions
      case TRANSITION_DECLINE:
      case TRANSITION_DECLINE_OFFER:
      case TRANSITION_CANCEL_PAYMENT:
      case TRANSITION_CANCEL_INVOICE:
      case TRANSITION_CANCEL:
      case TRANSITION_CANCEL_BY_PROVIDER:
        return intl.formatMessage({ id: 'NotificationFeed.newEvent' });

      // Expire transitions
      case TRANSITION_EXPIRE:
      case TRANSITION_EXPIRE_PAYMENT:
      case TRANSITION_EXPIRE_OFFER:
      case TRANSITION_EXPIRE_ENQUIRY:
      case TRANSITION_EXPIRE_APPLICATION:
        return intl.formatMessage({ id: 'NotificationFeed.newEvent' });

      // Application transitions
      case TRANSITION_ACCEPT_APPLICATION:
      case TRANSITION_ACCEPT_AFTER_SKIP:
        return intl.formatMessage({ id: 'NotificationFeed.applicationAccepted' });
      case TRANSITION_SKIP_APPLICATION:
        return intl.formatMessage({ id: 'NotificationFeed.applicationFreezed' });

      // Payment release
      case TRANSITION_RELEASE_FUNDS:
      case TRANSITION_JOB_DONE_RELEASE_FUNDS:
        return intl.formatMessage({ id: 'NotificationFeed.paymentReleased' });

      // Job completion
      case TRANSITION_JOB_DONE:
      case TRANSITION_JOB_DONE_CUSTOMER:
      case TRANSITION_JOB_DONE_INVOICE:
      case TRANSITION_JOB_DONE_PRO:
      case TRANSITION_COMPLETE:
        return intl.formatMessage({ id: 'NotificationFeed.jobDone' });

      case TRANSITION_JOB_ACCEPTED:
      case TRANSITION_ACCEPT_JOB_INVOICE:
      case TRANSITION_JOB_DONE_ACCEPT_INVOICE:
        return intl.formatMessage({ id: 'NotificationFeed.jobAccepted' });

      // Skip to reviews (Pro process)
      case TRANSITION_SKIP_TO_REVIEWS:
        return intl.formatMessage({ id: 'NotificationFeed.orderInProgress' });

      // Reviews
      case TRANSITION_REVIEW_1_BY_CUSTOMER:
      case TRANSITION_REVIEW_2_BY_CUSTOMER:
      case TRANSITION_REVIEW_1_BY_PROVIDER:
      case TRANSITION_REVIEW_2_BY_PROVIDER:
        return intl.formatMessage({ id: 'NotificationFeed.newReview' });

      default:
        return intl.formatMessage({ id: 'NotificationFeed.newEvent' });
    }
  };

  const todayString = intl.formatMessage({ id: 'ActivityFeed.today' });

  // Handle both old format (message object with attributes) and new format (plain string)
  const messageContent = isMessage
    ? typeof message.message === 'string'
      ? message.message
      : message.message.attributes.content
    : findTransitionTranslation(transaction.attributes.lastTransition);

  // Attachment-only messages store a sentinel token instead of typed text
  const content =
    messageContent === ATTACHMENTS_ONLY_MESSAGE
      ? intl.formatMessage({ id: 'ActivityFeed.attachmentsOnlyMessage' })
      : messageContent;

  const timeStamp = isMessage
    ? typeof message.message === 'string'
      ? formatDate(intl, todayString, transaction.attributes.lastTransitionedAt)
      : formatDate(intl, todayString, new Date(message.message.attributes.createdAt))
    : formatDate(intl, todayString, transaction.attributes.lastTransitionedAt);

  return (
    <div className={css.item}>
      <Avatar user={sender} />
      <NamedLink
        className={css.linkToDetails}
        name={isCustomerRole ? 'OrderDetailsPage' : 'SaleDetailsPage'}
        params={{ id: transaction.id.uuid }}
      >
        <div>
          <p className={css.message}>{content}</p>
          <p className={css.timeStamp}>{timeStamp}</p>
        </div>
      </NamedLink>
    </div>
  );
};

export default NotificationFeed;
